import { useEffect, useState } from 'react';

/**
 * Options for {@link useInViewport}.
 */
export interface UseInViewportOptions {
	/** Margin around the viewport that still counts as "in view" (CSS margin syntax). Default: '0px'. */
	rootMargin?: string;
	/** When true, stop observing after the element first becomes visible. Default: false. */
	once?: boolean;
}

/**
 * Tracks whether an element is inside the viewport (plus an optional margin).
 *
 * Attach the returned `ref` to the element you want to observe. The `ref` is a
 * stable callback so it is safe to pass straight to a JSX `ref` prop. Use
 * `once: true` for lazy-loading heavy content that should stay mounted after it
 * first appears; leave it false to mount/unmount as the element scrolls.
 *
 * @example
 * ```tsx
 * const { ref, isInViewport } = useInViewport({ rootMargin: '200px' });
 * return <div ref={ref}>{isInViewport ? <HeavyMap /> : <Skeleton />}</div>;
 * ```
 */
export const useInViewport = ({ rootMargin = '0px', once = false }: UseInViewportOptions = {}) => {
	const [element, setElement] = useState<Element | null>(null);
	const [isInViewport, setIsInViewport] = useState(false);

	useEffect(() => {
		if (!element) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry) return;

				if (entry.isIntersecting) {
					setIsInViewport(true);
					if (once) observer.disconnect();
					return;
				}

				if (!once) setIsInViewport(false);
			},
			{ rootMargin, threshold: 0 },
		);

		observer.observe(element);

		return () => observer.disconnect();
	}, [element, rootMargin, once]);

	return { ref: setElement, isInViewport };
};

export default useInViewport;
